/**
 * Audit log: who changed which fields of which document, and when.
 *
 * Entries live in `audit_log` and are written only with the Admin SDK:
 * - `change` entries by the `audit*` Firestore triggers in functions/index.js,
 *   one per document write, with the writer taken from the trigger's auth
 *   context (a browser write carries the member's uid);
 * - `action` entries by server routes (`recordAuditAction` in
 *   server/utils/auditLog.ts), because a write the server makes reaches the
 *   trigger as the server's service account, not as the admin who asked.
 *
 * Values are never recorded — only field paths. Point-in-time recovery (7 days)
 * and the daily backups (98 days) hold the values if they are ever needed.
 * Entries expire after AUDIT_RETENTION_DAYS through a TTL policy on `expireAt`.
 *
 * Pure functions only — synced into `functions/_shared` by
 * scripts/sync-shared-to-functions.mjs.
 */

export const AUDIT_LOG_COLLECTION = 'audit_log'
export const AUDIT_RETENTION_DAYS = 730

/** Deeper than this, a changed map is reported as one field. */
const MAX_FIELD_DEPTH = 3
const MAX_FIELDS = 100

/**
 * Bookkeeping that changes without anyone deciding anything. A write that
 * changes only these produces no entry.
 */
export const AUDIT_IGNORED_FIELDS: ReadonlySet<string> = new Set([
  'updated_at',
  'updatedAt',
  // Stamped by the SES webhook on every delivered or bounced email.
  'email_delivered_at',
  'email_bounce_type',
  'email_bounce_reason',
  'email_bounced_at',
  // Written back by onSubkreweDiscourseSync.
  'discourse_category_id',
])

export type AuditOp = 'create' | 'update' | 'delete'

/**
 * - `user`: a member, identified by uid (browser writes, and server actions
 *   recorded for the verified caller).
 * - `service`: a Google service account — the app server, a Cloud Function or
 *   a script. `principal` says which.
 * - `system`: Google itself, e.g. a TTL deletion.
 */
export type AuditActorKind = 'user' | 'service' | 'system' | 'unknown'

export interface AuditActor {
  kind: AuditActorKind
  uid?: string
  /** Snapshot at the time of the change, so renames don't rewrite history. */
  name?: string
  roles?: string[]
  memberType?: string
  /** Service account email, or whatever else the trigger reported. */
  principal?: string
  /** The trigger's raw authType, kept because its values are undocumented. */
  authType?: string
}

interface AuditEntryBase {
  at: Date
  expireAt: Date
  actor: AuditActor
  /** Documents whose history this entry belongs in, e.g. ['members/abc', 'subkrewes/xyz']. */
  refs: string[]
}

export interface AuditChangeEntry extends AuditEntryBase {
  type: 'change'
  path: string
  collection: string
  docId: string
  /** Name of the changed document (member, SubKrewe, product, task), for display. */
  label?: string
  op: AuditOp
  fields: string[]
  fieldsTruncated?: boolean
}

export interface AuditActionEntry extends AuditEntryBase {
  type: 'action'
  /** Dotted verb, e.g. 'pool.mint'. */
  action: string
  summary: string
  /** Ids and counts only — never personal data. */
  details?: Record<string, string | number | boolean | null>
}

export type AuditEntry = AuditChangeEntry | AuditActionEntry

export function auditExpiry(at: Date): Date {
  return new Date(at.getTime() + AUDIT_RETENTION_DAYS * 24 * 60 * 60 * 1000)
}

function isPlainMap(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

/** A comparable form of a Firestore value (Timestamps, references, maps, arrays). */
function normalize(value: unknown): unknown {
  if (value === undefined || value === null) return null
  if (Array.isArray(value)) return value.map(normalize)
  if (value instanceof Date) return { __ts: value.getTime() }
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>
    if (typeof v.toMillis === 'function') return { __ts: (v.toMillis as () => number)() }
    if (typeof v.path === 'string' && typeof v.id === 'string' && !isPlainMap(value)) return { __ref: v.path }
    if (typeof v.latitude === 'number' && typeof v.longitude === 'number' && !isPlainMap(value)) {
      return { __geo: [v.latitude, v.longitude] }
    }
    const out: Record<string, unknown> = {}
    for (const key of Object.keys(v).sort()) out[key] = normalize(v[key])
    return out
  }
  return value
}

function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b))
}

function collectChangedPaths(before: unknown, after: unknown, prefix: string, depth: number, out: string[]): void {
  if (depth < MAX_FIELD_DEPTH && isPlainMap(before ?? {}) && isPlainMap(after ?? {}) && (isPlainMap(before) || isPlainMap(after))) {
    const b = (before ?? {}) as Record<string, unknown>
    const a = (after ?? {}) as Record<string, unknown>
    for (const key of new Set([...Object.keys(b), ...Object.keys(a)])) {
      if (AUDIT_IGNORED_FIELDS.has(key)) continue
      collectChangedPaths(b[key], a[key], prefix ? `${prefix}.${key}` : key, depth + 1, out)
    }
    return
  }
  if (!sameValue(before, after)) out.push(prefix)
}

/**
 * Field paths that differ between two versions of a document, sorted. Maps are
 * followed three levels deep (`purchases.2027.membership`,
 * `approval_by_year.2027.status`); anything deeper, and arrays, count as one field.
 */
export function diffFieldPaths(before: Record<string, unknown> | null, after: Record<string, unknown> | null): string[] {
  const out: string[] = []
  collectChangedPaths(before ?? {}, after ?? {}, '', 0, out)
  return out.sort()
}

function refPath(value: unknown): string | null {
  if (typeof value === 'string' && value.includes('/')) return value
  if (value && typeof value === 'object' && typeof (value as { path?: unknown }).path === 'string') {
    return (value as { path: string }).path
  }
  return null
}

function stringField(data: Record<string, unknown> | null, key: string): string | null {
  const value = data?.[key]
  return typeof value === 'string' && value ? value : null
}

/**
 * Which histories a change shows up in: always the document itself, plus the
 * SubKrewe and member a document belongs to.
 */
export function auditRefs(
  path: string,
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
  fields: string[],
): string[] {
  const refs = new Set<string>([path])
  const segments = path.split('/')
  const collection = segments[0]

  // subkrewes/{sk}/dues_payments/{year}/entries/{member}
  if (collection === 'subkrewes' && segments.length >= 2) {
    refs.add(`subkrewes/${segments[1]}`)
    if (segments[2] === 'dues_payments' && segments[4] === 'entries' && segments[5]) {
      refs.add(`members/${segments[5]}`)
    }
  }

  // A member joining, leaving or becoming a captain belongs in the SubKrewe's history.
  const created = !before
  const deleted = !after
  if (collection === 'members' && (created || deleted || fields.some(f => f === 'subkrewe' || f === 'member_type'))) {
    for (const data of [before, after]) {
      const sk = refPath(data?.subkrewe)
      if (sk?.startsWith('subkrewes/')) refs.add(sk)
    }
  }

  if (collection === 'tasks') {
    for (const data of [before, after]) {
      const metadata = (data?.metadata ?? null) as Record<string, unknown> | null
      const subkreweId = stringField(metadata, 'subkreweId')
      if (subkreweId) refs.add(`subkrewes/${subkreweId}`)
      for (const key of ['memberId', 'captainId', 'requesterId']) {
        const memberId = stringField(metadata, key)
        if (memberId) refs.add(`members/${memberId}`)
      }
    }
  }

  if (collection === 'preapproved_members') {
    for (const data of [before, after]) {
      const subkreweId = stringField(data, 'subkreweId')
      if (subkreweId) refs.add(`subkrewes/${subkreweId}`)
    }
  }

  return [...refs].sort()
}

/** A readable name for the changed document — never an email or other contact detail. */
export function auditLabel(path: string, data: Record<string, unknown> | null): string | undefined {
  if (!data) return undefined
  const collection = path.split('/')[0]
  if (collection === 'members') {
    const full = [stringField(data, 'first_name'), stringField(data, 'last_name')].filter(Boolean).join(' ')
    return full || stringField(data, 'name') || undefined
  }
  if (collection === 'tasks') return stringField(data, 'title') || undefined
  return stringField(data, 'name') || undefined
}

/**
 * Who a trigger says made the write. Classified by the shape of `authId`
 * because the `authType` values are undocumented: an email-like principal is a
 * service account (or a console user), anything else is a member uid.
 */
export function classifyAuditActor({ authType, authId }: { authType?: string | null, authId?: string | null }): AuditActor {
  const raw = authType ? { authType } : {}
  if (authType === 'system') return { kind: 'system', ...raw, ...(authId ? { principal: authId } : {}) }
  if (!authId) return { kind: 'unknown', ...raw }
  if (authId.includes('@')) {
    return { kind: authId.endsWith('.gserviceaccount.com') ? 'service' : 'unknown', principal: authId, ...raw }
  }
  return { kind: 'user', uid: authId, ...raw }
}

/** Name and roles to snapshot for a member acting. */
export function describeAuditMember(data: Record<string, unknown> | null): Pick<AuditActor, 'name' | 'roles' | 'memberType'> {
  if (!data) return {}
  const roles = data.roles && typeof data.roles === 'object' && !Array.isArray(data.roles)
    ? Object.entries(data.roles as Record<string, unknown>).filter(([, on]) => on === true).map(([role]) => role).sort()
    : []
  const name = auditLabel('members/x', data)
  const memberType = stringField(data, 'member_type')
  return {
    ...(name ? { name } : {}),
    roles,
    ...(memberType ? { memberType } : {}),
  }
}

/** The entry for one document write, or null when nothing worth recording changed. */
export function buildAuditChangeEntry({
  path,
  before,
  after,
  actor,
  at,
}: {
  path: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  actor: AuditActor
  at: Date
}): AuditChangeEntry | null {
  if (!before && !after) return null
  const op: AuditOp = !before ? 'create' : !after ? 'delete' : 'update'

  let fields: string[]
  if (op === 'create') fields = Object.keys(after ?? {}).filter(key => !AUDIT_IGNORED_FIELDS.has(key)).sort()
  else if (op === 'delete') fields = []
  else fields = diffFieldPaths(before, after)

  if (op === 'update' && fields.length === 0) return null

  const segments = path.split('/')
  const truncated = fields.length > MAX_FIELDS
  const label = auditLabel(path, after ?? before)
  return {
    type: 'change',
    at,
    expireAt: auditExpiry(at),
    actor,
    refs: auditRefs(path, before, after, fields),
    path,
    collection: segments[segments.length - 2] ?? segments[0] ?? '',
    docId: segments[segments.length - 1] ?? '',
    ...(label ? { label } : {}),
    op,
    fields: truncated ? fields.slice(0, MAX_FIELDS) : fields,
    ...(truncated ? { fieldsTruncated: true } : {}),
  }
}

/** The entry for an action a server route took on someone's behalf. */
export function buildAuditActionEntry({
  action,
  summary,
  refs,
  details,
  actor,
  at,
}: {
  action: string
  summary: string
  refs: string[]
  details?: Record<string, string | number | boolean | null | undefined>
  actor: AuditActor
  at: Date
}): AuditActionEntry {
  const cleanDetails = details
    ? Object.fromEntries(Object.entries(details).filter(([, v]) => v !== undefined)) as Record<string, string | number | boolean | null>
    : undefined
  return {
    type: 'action',
    at,
    expireAt: auditExpiry(at),
    actor,
    refs: [...new Set(refs)].sort(),
    action,
    summary,
    ...(cleanDetails && Object.keys(cleanDetails).length ? { details: cleanDetails } : {}),
  }
}
